import { React, useState, useEffect } from "react";
import { api } from "../../../../../utils/api";
import { useParams, useNavigate } from "react-router-dom";
import { Button } from "@mui/material";
import ModalPopup from "../../../../../components/ModalPopup";

import GlobalLoadingSpinner from "../../../../../components/GlobalLoadingSpinner";
import BasicUserInfo from "../../../../../components/BasicUserInfo";
import MerchantUserForm from "../../../../../components/Forms/MerchantUserForm";
import IconButton from "@mui/material/IconButton";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { COLORS } from "../../../../../utils/colors";

const MerchantUserSingle = () => {
  let navigate = useNavigate();
  const params = useParams();
  const [loading, setLoading] = useState(true);
  const [editMerchantUserState, setEditMerchantUserState] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [inactiveConfirmationIsOpen, setInactiveConfirmationIsOpen] = useState(false);
  const [deleteConfirmationIsOpen, setDeleteConfirmationIsOpen] = useState(false);

  useEffect(() => {
    fetchUserDetails();
  }, []);

  const fetchUserDetails = async () => {
    try {
      const response = await api(`v1/merchant/${params.idMerchant}/user/${params.idUser}`, "GET", null);
      if (response.data.success) {
        setSelectedUser(response.data.data.merchantUser);
        setLoading(false);
      }
    } catch (error) {
      console.error("API error:", error);
    }
  };

  const handleMakeInactiveConfirm = async () => {
    setLoading(true);
    try {
      const response = await api(
        `v1/merchant/${params.idMerchant}/user/${params.idUser}/deactivate`,
        "POST",
        null
      );
      if (response.data.success) {
        alert(response.data.message);
        navigate(`/merchant/${params.idMerchant}?tab=users`);
      } else if (response.data.message) {
        alert(response.data.message);
      }
    } catch (error) {
      console.error("API error:", error);
      alert(error?.response?.data?.message || "Could not deactivate merchant user.");
    }
    setInactiveConfirmationIsOpen(false);
    setLoading(false);
  };

  const handleDeleteUserConfirm = async () => {
    setLoading(true);
    try {
      const response = await api(`v1/merchant/${params.idMerchant}/user/${params.idUser}`, "DELETE", null);
      if (response.data.success) {
        alert(response.data.message);
        navigate(`/merchant/${params.idMerchant}?tab=users`);
      } else if (response.data.message) {
        alert(response.data.message);
      }
    } catch (error) {
      console.error("API error:", error);
      alert(error?.response?.data?.message || "Could not delete merchant user.");
    }
    setDeleteConfirmationIsOpen(false);
    setLoading(false);
  };

  const editMerchantUser = () => {
    setEditMerchantUserState((prevValue) => !prevValue);
  };

  const isActive = selectedUser?.active !== false;

  return (
    selectedUser && (
      <div className="page partner-user-single  w-full flex flex-column items-center min-h-h-screen p-4 gap-8">
        {loading && <GlobalLoadingSpinner />}
        <ModalPopup
          showModal={editMerchantUserState}
          handleShowModal={editMerchantUser}
          popupTitle={"Edit Merchant User"}
          popupContent={
            <MerchantUserForm
              merchant={{ _id: params.idMerchant }}
              merchantUser={selectedUser}
              fetchUserDetails={fetchUserDetails}
              handleDisplayModal={editMerchantUser}
            />
          }
        />

        <div className="w-full flex flex-row justify-between items-center">
          <div>
            <h1 className="font-bold">
              {selectedUser.firstName} {selectedUser.lastName}
              {!isActive && (
                <span className="ml-3 text-sm font-semibold text-red-600 align-middle">(Inactive)</span>
              )}
            </h1>
          </div>
          <div>
            <IconButton onClick={() => navigate(-1)} sx={{ border: "1px solid grey" }}>
              <ArrowBackIcon />
            </IconButton>
          </div>
        </div>

        {selectedUser && (
          <div className="w-full">
            <div className="flex flex-column gap-2">
              <BasicUserInfo user={selectedUser} merchantId={params.idMerchant || params.id} />
            </div>

            <div className="actionItems flex flex-wrap gap-4">
              <Button
                sx={{ backgroundColor: COLORS.priorityBlue.hex }}
                className="self-start m-4"
                variant="contained"
                onClick={editMerchantUser}
              >
                Edit Merchant User
              </Button>
              {isActive && (
                <Button
                  className="self-start m-4"
                  variant="outlined"
                  color="warning"
                  onClick={() => setInactiveConfirmationIsOpen(true)}
                >
                  Make Inactive
                </Button>
              )}
              <Button
                className="self-start bg-red-600 m-4"
                variant="contained"
                color="error"
                onClick={() => setDeleteConfirmationIsOpen(true)}
              >
                Delete Permanently
              </Button>
            </div>

            <ModalPopup
              showModal={inactiveConfirmationIsOpen}
              handleShowModal={() => setInactiveConfirmationIsOpen(false)}
              popupTitle={"Make User Inactive"}
              popupContent={
                <div>
                  Make{" "}
                  <strong>
                    <em>
                      {selectedUser.firstName} {selectedUser.lastName}
                    </em>
                  </strong>{" "}
                  inactive? They will lose portal access and be removed from any store staff lists on
                  this account. The record is kept so you can review duplicates before deleting.
                </div>
              }
              cancelButtonText={"Cancel"}
              confirmButtonText={"Make Inactive"}
              confirmCallbackAction={handleMakeInactiveConfirm}
            />

            <ModalPopup
              showModal={deleteConfirmationIsOpen}
              handleShowModal={() => setDeleteConfirmationIsOpen(false)}
              popupTitle={"Delete User Permanently"}
              popupContent={
                <div>
                  Permanently delete{" "}
                  <strong>
                    <em>
                      {selectedUser.firstName} {selectedUser.lastName}
                    </em>
                  </strong>
                  ? This cannot be undone. The user will be removed from store staff lists on this
                  account. Historical redemptions will keep their existing reference.
                </div>
              }
              cancelButtonText={"Cancel"}
              confirmButtonText={"Delete Permanently"}
              confirmCallbackAction={handleDeleteUserConfirm}
            />
          </div>
        )}
      </div>
    )
  );
};

export default MerchantUserSingle;
